import React, { useEffect, useRef, useState } from "react";
import { Form, Select } from "antd";
import { dataElementList, selectedBudgetList } from "../../../actions/admin";
import { useDispatch, useSelector } from "react-redux";
import { userDataElementListById } from "../../../actions/user";

// Define interface for pagination params
interface PaginationParams {
  page?: number;
  limit?: number;
}

interface listingInitialData {
  dataToSet: any;
  keyName: string;
  name: string;
  setBudgetDescription?: (value: any) => void; // Optional setter function
  selectedOrgId?: string;
  setIntervalId?: (value: any) => void;
  apiType?: string;
}

const { Option } = Select;
const isDisabled = ["editDataEntry"];

const DataElementsListing: React.FC<listingInitialData> = ({
  dataToSet,
  keyName,
  name,
  setBudgetDescription,
  selectedOrgId,
  setIntervalId,
  apiType,
}) => {
  const [data, setData] = useState<any[]>([]);
  const [form] = Form.useForm();
  const dispatch: any = useDispatch();
  const [loading, setLoading] = useState<boolean>(true);
  const user = useSelector((state: any) => state?.Auth?.user);
  const roleId = user?.user?.roleId;
  const prevOrgIdRef = useRef<string | undefined>(selectedOrgId);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 7,
    total: 0,
    totalPages: 0,
  });

  const getSelectetDataElement = async (id) => {
    setLoading(true);
    dispatch(
      selectedBudgetList(id, (resp: any) => {
        setLoading(false);
        const oldData = [...data, resp?.data?.dataElement];
        const uniqueData = oldData?.filter(
          (value, index, self) =>
            index === self.findIndex((org) => org?.id === value?.id)
        );
        setData(uniqueData);
      })
    );
  };

  useEffect(() => {
    if (dataToSet?.id) getSelectetDataElement(dataToSet?.id);
  }, [dataToSet, selectedOrgId]);

  // Fetches the list of organizations with optional query parameters
  const fetchDataElementList = (params: PaginationParams = {}) => {
    setLoading(true);
    if ([1, 2, 3].includes(roleId) && apiType != "user") {
      const payload = { params, isBudget: 1 };
      dispatch(
        dataElementList({ payload, roleId }, (resp: any) => {
          setLoading(false);
          if (resp?.status === 200) {
            // setData(resp?.data || []);
            const newData = resp?.data || [];

            const oldData = [...data, ...newData];

            setData(oldData);

            setPagination({
              ...pagination,
              total: resp?.pagination?.totalItems,
              current: resp?.pagination?.currentPage,
              pageSize: resp?.pagination?.pageSize,
              totalPages: resp?.pagination?.totalPages,
            });
          }
        })
      );
    } else {
      // this is for the user section
      // if (selectedOrgId) {
      //   const updatedParams = { ...params, organizationId: selectedOrgId };
      //   dispatch(
      //     userDataElementListById(updatedParams, (resp: any) => {
      //       setLoading(false);
      //       if (resp?.status === 200) {
      //         const newData = resp?.data || [];

      //         const oldData = [...data, ...newData];
      //         setIntervalId(oldData);

      //         setData(oldData);

      //         setPagination({
      //           ...pagination,
      //           total: resp.pagination?.totalItems,
      //           current: resp.pagination?.currentPage,
      //           pageSize: resp.pagination?.pageSize,
      //           totalPages: resp.pagination?.totalPages,
      //         });
      //       }
      //     })
      //   );
      // }
      if (selectedOrgId) {
        const updatedParams = {
          ...params,
          organizationId: selectedOrgId,
          sort: "DataElements_Name",
          order: "asc",
        };
        // const updatedParams = { ...params, organizationId: selectedOrgId };
        dispatch(
          userDataElementListById(updatedParams, (resp: any) => {
            setLoading(false);
            if (resp?.status === 200) {
              const newData = resp?.data || [];

              // If the selectedOrgId hasn't changed, append to oldData
              const shouldAppend = prevOrgIdRef.current === selectedOrgId;
              const updatedData = shouldAppend
                ? [...data, ...newData]
                : newData;

              setIntervalId(updatedData);
              setData(updatedData);

              setPagination({
                ...pagination,
                total: resp.pagination?.totalItems,
                current: resp.pagination?.currentPage,
                pageSize: resp.pagination?.pageSize,
                totalPages: resp.pagination?.totalPages,
              });

              // Update the reference to the current orgId
              prevOrgIdRef.current = selectedOrgId;
            }
          })
        );
      }
    }
  };

  // Load initial data
  useEffect(() => {
    fetchDataElementList({
      page: 1,
      limit: pagination.pageSize,
    });
  }, [selectedOrgId]);

  // Handle onPopupScroll event for the Select component
  const handleSelectScroll = (e: React.UIEvent<HTMLDivElement>) => {
    // Check if user has scrolled to the bottom
    const { target } = e;
    if (
      !loading &&
      target instanceof HTMLElement &&
      target.scrollTop + target.offsetHeight >= target.scrollHeight - 20 &&
      pagination.current < pagination.totalPages
    ) {
      // Load next page
      const nextPage = pagination.current + 1;
      fetchDataElementList({
        page: nextPage,
        limit: pagination.pageSize,
      });
    }
  };

  return (
    <div className="mb-[10px]">
      <Form.Item
        name={keyName}
        rules={[{ required: true, message: "Please select a Data Element" }]}
      >
        <Select
          placeholder="Data Element"
          className="input-main-col focus:bg-transparent rounded-[5px] mb-[10px] p-[10px] border-1 border-transparent focus:border-white w-full bg-[#013351] placeholder-[#005A90] color-[#fff] h-auto"
          onPopupScroll={handleSelectScroll}
          loading={loading}
          notFoundContent={loading ? "Loading..." : "No data found"}
          listHeight={200}
          allowClear // ✅ Allows users to clear selections
          // showSearch // ✅ Enables searching
          disabled={
            Object.keys(dataToSet || {}).length !== 0 ||
            isDisabled.includes(name)
          }
          onChange={(value) => {
            const selectedElement = data?.find(
              (element) => element.id === value
            );
            if (selectedElement && setBudgetDescription) {
              setBudgetDescription(selectedElement.DataElements_Description);
            }
          }}
        >
          <Option key="" value="">
            Data Element
          </Option>
          {data?.map((element) => (
            <Option
              key={element?.id}
              value={element?.id}
              style={{
                backgroundColor: "#013351",
                border: "none",
              }}
            >
              <div
                style={{ display: "flex", flexDirection: "row", gap: "2px" }}
              >
                {element?.DataElements_Name}
              </div>
            </Option>
          ))}
        </Select>
      </Form.Item>
    </div>
  );
};

export default DataElementsListing;
